import React, { useEffect, useState } from 'react';
import styles from './styles.module.css';
import rectangleXmark from '../../../ExpandedRecordDr6/images/rectangle-xmark.svg';
import bracketsCurly from '../../../ExpandedRecordDr6/images/brackets-curly-solid.svg';
import JsonViewModal from '../../../viewJson';
import useEscapeKey from '../../../utils/useEscapeKey';

function CompactPropertyView({ record, onClose, isLoadingRecord }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isJsonModalVisible, setIsJsonModalVisible] = useState(false);

  useEffect(() => {
    // Always trigger slide-in animation, whether we have a record or not
    const timeoutId = setTimeout(() => {
      setIsVisible(true);
    }, 1);
    return () => clearTimeout(timeoutId);
  }, []);

  const handleClose = () => {
    if (!isJsonModalVisible) {
      setIsVisible(false);
      setTimeout(() => {
        onClose();
      }, 250);
    }
  };

  useEscapeKey(handleClose);

  const toggleJsonModal = () => {
    setIsJsonModalVisible(true);
  };

  if (isLoadingRecord) {
    return (
      <div className={styles.modalBackdrop} onClick={handleClose}>
        <div
          className={styles.modalContent}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: isVisible ? 'translateX(0)' : 'translateX(100%)'
          }}
          onClick={e => e.stopPropagation()}
        >
          <p>Loading property details...</p>
        </div>
      </div>
    );
  }

  // If no record found, show "No Records Found"
  if (!record) {
    return (
      <div className={styles.modalBackdrop} onClick={handleClose}>
        <div
          className={styles.modalContent}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: isVisible ? 'translateX(0)' : 'translateX(100%)'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '16px', color: '#000000', fontWeight: 500 }}>No Records Found</p>
            <button
              onClick={handleClose}
              style={{
                marginTop: '16px',
                padding: '8px 16px',
                backgroundColor: '#8122B4',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to format dates - matches full property modal format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return 'N/A';
      return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
    } catch (error) {
      return 'N/A';
    }
  };

  // Helper function to get status from price type - matches full property modal
  const getPriceStatus = (price) => {
    if (price?.type) {
      switch (price.type) {
        case 'Sale List':
          return 'For Sale';
        case 'Sale':
          return 'Sold';
        case 'Rental List':
          return 'For Rent';
        default:
          return price.type;
      }
    }
    return 'N/A';
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };


  // Get property address
  const getFullAddress = () => {
    const parts = [];
    if (record?.address) parts.push(record.address);
    if (record?.city) parts.push(record.city);
    if (record?.province) parts.push(record.province);
    if (record?.postalCode) parts.push(record.postalCode);
    return parts.length > 0 ? parts.join(', ') : 'N/A';
  };

  return (
    <div className={styles.modalBackdrop} onClick={handleClose}>
      <div
        className={styles.modalContent}
        style={{ transform: isVisible ? 'translateX(0)' : 'translateX(100%)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>{record.address || 'Property Details'}</h1>
            <h2 className={styles.subtitle}>
              {record.city && record.province
                ? `${record.city}, ${record.province} ${record.postalCode || ''}`
                : getFullAddress()}
            </h2>
          </div>
          <div className={styles.headerButtons}>
            <div className={styles.buttonContainer} onClick={handleClose}>
              <span className={styles.buttonText}>Close</span>
              <img alt="close" src={rectangleXmark} className={styles.icon} />
            </div>
            <div className={styles.buttonContainer} onClick={toggleJsonModal}>
              <span className={styles.buttonTextPurple}>View JSON</span>
              <img alt="json" src={bracketsCurly} className={styles.icon} />
            </div>
          </div>
        </div>

        {/* Property Details Grid */}
        <div className={styles.detailsGrid}>
          {/* Property Type */}
          <div className={styles.detailItem}>
            <span className={styles.label}>Property type</span>
            <span className={styles.value}>{record?.propertyType || 'N/A'}</span>
          </div>

          {/* Floor Size */}
          <div className={styles.detailItem}>
            <span className={styles.label}>Floor size</span>
            <span className={styles.value}>
              {record?.floorSizeValue ? `${record.floorSizeValue} ${record.floorSizeUnit}` : 'N/A'}
            </span>
          </div>

          {/* Lot Size */}
          <div className={styles.detailItem}>
            <span className={styles.label}>Lot size</span>
            <span className={styles.value}>
              {record?.lotSizeValue ? `${record.lotSizeValue} ${record.lotSizeUnit}` : 'N/A'}
            </span>
          </div>

          {/* Bedrooms */}
          <div className={styles.detailItem}>
            <span className={styles.label}>Bed</span>
            <span className={styles.value}>{record?.numBedroom || 'N/A'}</span>
          </div>

          {/* Bathrooms */}
          <div className={styles.detailItem}>
            <span className={styles.label}>Bath</span>
            <span className={styles.value}>{record?.numBathroom || 'N/A'}</span>
          </div>
        </div>

        <hr className={styles.hr} />

        {/* Most Recent Data */}
        <div className={styles.section}>
          <div className={styles.detailsGrid}>
            {/* Most Recent Status */}
            <div className={styles.detailItem}>
              <span className={styles.label}>Most recent status</span>
              <span className={styles.value}>{record?.mostRecentStatus || 'N/A'}</span>
            </div>

            {/* Most Recent Sale List Price */}
            <div className={styles.detailItem}>
              <span className={styles.label}>Most recent sale list price</span>
              <span className={styles.value}>
                {record?.mostRecentSaleListPriceAmount || record?.mostRecentPriceAmount
                  ? formatCurrency(record.mostRecentSaleListPriceAmount || record.mostRecentPriceAmount)
                  : 'N/A'}
              </span>
            </div>

            {/* Most Recent Sold Price */}
            <div className={styles.detailItem}>
              <span className={styles.label}>Most recent sold price</span>
              <span className={styles.value}>
                {record?.mostRecentSoldPriceAmount
                  ? formatCurrency(record.mostRecentSoldPriceAmount)
                  : 'N/A'}
              </span>
            </div>

            {/* Most Recent Rental List Price */}
            <div className={styles.detailItem}>
              <span className={styles.label}>Most recent rental list price</span>
              <span className={styles.value}>
                {record?.mostRecentRentalListPriceAmount || record?.mostRecentRentalPriceAmount
                  ? formatCurrency(record.mostRecentRentalListPriceAmount || record.mostRecentRentalPriceAmount)
                  : 'N/A'}
              </span>
            </div>

            {/* Most Recent Agent */}
            <div className={styles.detailItem}>
              <span className={styles.label}>Most recent agent</span>
              <span className={styles.value}>
                {record?.mostRecentBrokerAgent || 'N/A'}
              </span>
            </div>

            {/* Most Recent Owner */}
            <div className={styles.detailItem}>
              <span className={styles.label}>Most recent owner</span>
              <span className={styles.value}>
                {record?.mostRecentOwnerFirstName || record?.mostRecentOwnerLastName
                  ? `${record.mostRecentOwnerFirstName || ''} ${record.mostRecentOwnerLastName || ''}`.trim()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        <hr className={styles.hr} />

        {/* Price History */}
        {(() => {
          // Sort and deduplicate prices - matches full property modal logic
          const sortedPrices = record?.prices
            ? [
                // First, entries with dates sorted by date descending, deduplicated by date
                ...record.prices
                  .filter(price => price.date)
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .filter(
                    (price, index, self) =>
                      index === self.findIndex(p => p.date === price.date)
                  ),
                // Then, entries without dates sorted by amountMax, deduplicated by amountMax
                ...record.prices
                  .filter(price => !price.date)
                  .sort((a, b) => (b.amountMax || 0) - (a.amountMax || 0))
                  .filter(
                    (price, index, self) =>
                      index === self.findIndex(p => p.amountMax === price.amountMax)
                  )
              ]
            : [];

          return sortedPrices.length > 0 ? (
            <>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Price history</h3>
                <div className={styles.historyTable}>
                  {sortedPrices.slice(0, 5).map((price, index) => (
                    <div key={index} className={styles.historyRow}>
                      <span className={styles.historyValue}>
                        {formatCurrency(price.amountMax)}
                      </span>
                      <span className={styles.historyDate}>
                        {formatDate(price.date)}
                      </span>
                      <span className={styles.historyType}>
                        {getPriceStatus(price)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <hr className={styles.hr} />
            </>
          ) : null;
        })()}

        {/* Status History */}
        {(() => {
          // Filter, sort, and deduplicate statuses - matches full property modal logic
          const filteredStatuses = record?.statuses?.filter(status => status.date) || [];
          const sortedStatuses = filteredStatuses.sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          );
          // Remove duplicates by normalized date (YYYY-MM-DD)
          const uniqueStatuses = sortedStatuses.filter(
            (status, index, self) =>
              index ===
              self.findIndex(
                s =>
                  new Date(s.date).toISOString().split('T')[0] ===
                  new Date(status.date).toISOString().split('T')[0]
              )
          );

          return uniqueStatuses.length > 0 ? (
            <>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Status history</h3>
                <div className={styles.historyTable}>
                  {uniqueStatuses.slice(0, 5).map((status, index) => (
                    <div key={index} className={styles.statusRow}>
                      <span className={styles.statusValue}>{status.type || 'N/A'}</span>
                      <span className={styles.statusDate}>
                        {formatDate(status.date)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <hr className={styles.hr} />
            </>
          ) : null;
        })()}

        {/* Legal Description */}
        {record?.legalDescription && (
          <>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Legal description</h3>
              <p className={styles.legalText}>{record.legalDescription}</p>
              <div className={styles.legalDetails}>
                {record?.parcelNumbers?.[0]?.number && (
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Parcel number</span>
                    <span className={styles.value}>{record.parcelNumbers[0].number}</span>
                  </div>
                )}
                {record?.mlsNumber && (
                  <div className={styles.detailItem}>
                    <span className={styles.label}>MLS #</span>
                    <span className={styles.value}>{record.mlsNumber}</span>
                  </div>
                )}
                {record?.taxID && (
                  <div className={styles.detailItem}>
                    <span className={styles.label}>Tax ID</span>
                    <span className={styles.value}>{record.taxID}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {isJsonModalVisible && (
        <JsonViewModal
          jsonData={record}
          onClose={() => setIsJsonModalVisible(false)}
          isPropertyFunctionality={true}
        />
      )}
    </div>
  );
}

export default CompactPropertyView;
